import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { useWizard } from "../../context/WizardContext";
import { DateInput } from "../shared/DateInput";
import type { VehicleInfo } from "../../types/business.types";

const yesNo = (v: boolean | undefined): string => {
  if (v === true) return "yes";
  if (v === false) return "no";
  return "";
};

export function BusinessStep5Vehicle() {
  const { businessData, updateBusinessData } = useWizard();
  const v = businessData.vehicleInfo ?? {} as VehicleInfo;

  const set = (patch: Partial<VehicleInfo>) => {
    updateBusinessData({ vehicleInfo: { ...v, ...patch } });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Vehicle Information</h2>
        <p className="text-sm text-muted-foreground">
          Only relevant if you&apos;re claiming car or truck expenses.
        </p>
      </div>

      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertDescription>
          Complete this section only if you are claiming car/truck expenses.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Vehicle Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DateInput
            label="Date vehicle placed in service"
            value={v.placedInServiceDate}
            onChange={(val) => set({ placedInServiceDate: val })}
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="totalMiles">Total miles driven</Label>
              <Input
                id="totalMiles"
                type="number"
                min={0}
                inputMode="numeric"
                value={v.totalMiles ?? ""}
                onChange={(e) => set({ totalMiles: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="businessMiles">Business miles</Label>
              <Input
                id="businessMiles"
                type="number"
                min={0}
                inputMode="numeric"
                value={v.businessMiles ?? ""}
                onChange={(e) => set({ businessMiles: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="personalMiles">Commuting miles</Label>
              <Input
                id="personalMiles"
                type="number"
                min={0}
                inputMode="numeric"
                value={v.personalMiles ?? ""}
                onChange={(e) => set({ personalMiles: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="0"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Was the vehicle available for personal use?</Label>
            <RadioGroup
              value={yesNo(v.vehicleAvailableForPersonalUse)}
              onValueChange={(val) => set({ vehicleAvailableForPersonalUse: val === "yes" })}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="v-personal-yes" />
                <Label htmlFor="v-personal-yes" className="font-normal">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="v-personal-no" />
                <Label htmlFor="v-personal-no" className="font-normal">No</Label>
              </div>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label>Was another vehicle available for personal use?</Label>
            <RadioGroup
              value={yesNo(v.anotherVehicleAvailable)}
              onValueChange={(val) => set({ anotherVehicleAvailable: val === "yes" })}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="v-other-yes" />
                <Label htmlFor="v-other-yes" className="font-normal">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="v-other-no" />
                <Label htmlFor="v-other-no" className="font-normal">No</Label>
              </div>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label>Do you have evidence to support the deduction?</Label>
            <RadioGroup
              value={yesNo(v.hasSupportingEvidence)}
              onValueChange={(val) => set({ hasSupportingEvidence: val === "yes" })}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="v-evid-yes" />
                <Label htmlFor="v-evid-yes" className="font-normal">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="v-evid-no" />
                <Label htmlFor="v-evid-no" className="font-normal">No</Label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
